import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import Footer from "../../layout/Footer";
import Navbar from "../../layout/Navbar";

const VerifiedTutors = () => {
    const [tutors, setTutors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTutor, setSelectedTutor] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("all");
    const [selectedLevel, setSelectedLevel] = useState("all");
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [bookingForm, setBookingForm] = useState({
        phone: "",
        subject: "",
        date: "",
        time: "",
        duration: "1",
        mode: "online"
    });
    const [stats, setStats] = useState({
        totalTutors: 0,
        averageRating: 4.8,
        subjectsCount: 0,
        satisfactionRate: 95
    });

    const API_URL = "https://pastq.eddcodesgh.com/qApi";
    const API_KEY = "f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506";

    // Available subjects for filtering
    const subjects = [
        "Mathematics", "Physics", "Biology", "Chemistry", "Cybersecurity", 
        "Creative Art", "Science", "English Language & Literature", 
        "Social Studies", "ICT/Computer Studies", "Business Studies", 
        "Accounting", "Economics", "Government", "Geography", 
        "History", "French", "Technical Drawing", "Home Economics", 
        "Religious & Moral Education"
    ];

    const educationLevels = ["JHS/BECE", "SHS/WASSCE", "CTVET", "University", "Professional"];

    // Fetch tutors from API with useCallback to prevent unnecessary re-renders
    const fetchVerifiedTutors = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/client/tutors/_verified-tutors.php`, {
                headers: {
                    'Authorization': `Bearer ${API_KEY}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.status === 'success') {
                const tutorsData = response.data.data;
                setTutors(tutorsData);
                
                // Calculate stats
                const totalTutors = tutorsData.length;
                const avgRating = tutorsData.length > 0 
                    ? tutorsData.reduce((sum, tutor) => sum + (parseFloat(tutor.rating) || 0), 0) / tutorsData.length 
                    : 4.8;
                
                // Count unique subjects
                const uniqueSubjects = new Set();
                tutorsData.forEach(tutor => {
                    if (tutor.subjects) {
                        if (typeof tutor.subjects === 'string') {
                            try {
                                const subjectsArray = JSON.parse(tutor.subjects);
                                subjectsArray.forEach(subject => uniqueSubjects.add(subject));
                            } catch (e) {
                                // If it's not JSON, split by comma
                                tutor.subjects.split(',').forEach(subject => uniqueSubjects.add(subject.trim()));
                            }
                        }
                    }
                });

                setStats({
                    totalTutors,
                    averageRating: avgRating.toFixed(1),
                    subjectsCount: uniqueSubjects.size,
                    satisfactionRate: 95
                });
            } else {
                setError(response.data.message || "Failed to fetch tutors");
            }
        } catch (err) {
            setError(err.response?.data?.message || "Network error. Please try again.");
            toast.error("Failed to load tutors. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [API_KEY, API_URL]);

    useEffect(() => {
        fetchVerifiedTutors();
    }, [fetchVerifiedTutors]);

    // Helper function to parse expertise/education level
    const parseArrayData = useCallback((data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch (e) {
                return data.split(',').map(item => item.trim());
            }
        }
        return [];
    }, []);

    // Format tutor data for display with useCallback
    const formatTutorForDisplay = useCallback((tutor) => {
        const expertise = parseArrayData(tutor.expertise);
        const educationLevel = parseArrayData(tutor.education_level);
        const tutorSubjects = parseArrayData(tutor.subjects);

        return {
            id: tutor.id,
            name: tutor.full_name || "Unnamed Tutor",
            title: tutor.qualification || "Tutor",
            phone: tutor.phone,
            email: tutor.email,
            rating: parseFloat(tutor.rating) || 0,
            reviews: tutor.total_sessions || 0,
            subjects: tutorSubjects.length > 0 ? tutorSubjects : ["General Tutoring"],
            levels: educationLevel.length > 0 ? educationLevel : ["All Levels"],
            experience: tutor.experience || "Not specified",
            hourlyRate: `₵${tutor.hourly_rate || "30"}`,
            availability: tutor.availability || "Flexible",
            mode: tutor.availability === "online" ? ["Online"] : ["Online", "In-person"],
            location: tutor.region || "Ghana",
            education: tutor.qualification || "Not specified",
            bio: tutor.teaching_philosophy || "No bio available",
            verified: tutor.verified === 1 || tutor.status === "active",
            profileImage: tutor.profile_image || "/assets/img/default-avatar.jpg",
            verifiedBadge: tutor.verified === 1,
            featured: tutor.rating >= 4.5,
            expertise: expertise
        };
    }, [parseArrayData]);

    const filteredTutors = React.useMemo(() => {
        return tutors
            .map(formatTutorForDisplay)
            .filter(tutor => {
                const matchesSearch = searchTerm === "" || 
                    tutor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    tutor.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    tutor.subjects.some(subject => subject.toLowerCase().includes(searchTerm.toLowerCase()));
                
                const matchesSubject = selectedSubject === "all" || 
                    tutor.subjects.some(subject => subject === selectedSubject);
                
                const matchesLevel = selectedLevel === "all" || 
                    tutor.levels.some(level => level === selectedLevel);
                
                return matchesSearch && matchesSubject && matchesLevel;
            });
    }, [tutors, formatTutorForDisplay, searchTerm, selectedSubject, selectedLevel]);

    const handleBookTutor = useCallback((tutor) => {
        setSelectedTutor(tutor);
        setBookingForm({
            ...bookingForm,
            subject: tutor.subjects[0]
        });
        setShowBookingModal(true);
    }, [bookingForm]);

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const bookingData = {
                tutor_id: selectedTutor.id,
                phone: bookingForm.phone,
                subject: bookingForm.subject,
                date: bookingForm.date,
                time: bookingForm.time,
                duration: bookingForm.duration,
                mode: bookingForm.mode,
                status: 'pending'
            };

            const response = await axios.post(`${API_URL}/client/tutors/_bookings.php`, bookingData, {
                headers: {
                    'Authorization': `Bearer ${API_KEY}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                toast.success(`Booking request sent to ${selectedTutor.name}! They will contact you shortly.`);
                setShowBookingModal(false);
                setBookingForm({
                     phone: "",
                    subject: "",
                    date: "",
                    time: "",
                    duration: "1",
                    mode: "online"
                });
            } else {
                toast.error(response.data.message || "Failed to create booking");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to create booking. Please try again.");
        }
    };

    const StatsCard = ({ icon, number, label, color }) => (
        <div className="col-md-3">
            <div className="card border-0 shadow-sm">
                <div className="card-body text-center">
                    <div className={`icon-wrapper rounded-circle d-inline-flex align-items-center justify-content-center mb-3`}
                         style={{ 
                             width: '60px', 
                             height: '60px', 
                             background: `var(--bs-${color}-bg-subtle)`,
                             color: `var(--bs-${color})`
                         }}>
                        <i className={`bi ${icon} fs-3`}></i>
                    </div>
                    <h3 className="fw-bold mb-1">{number}</h3>
                    <p className="text-muted mb-0">{label}</p>
                </div>
            </div>
        </div>
    );

    const TutorCard = React.memo(({ tutor, onViewProfile, onBookTutor }) => (
        <div className="col-md-6 col-lg-4 mb-4">
            <div className="card border-0 shadow-sm h-100 tutor-card">
                <div className="card-body position-relative">
                    {/* Featured Badge */}
                    {tutor.featured && (
                        <div className="position-absolute top-0 end-0 m-3">
                            <span className="badge bg-warning text-dark">
                                <i className="bi bi-star-fill me-1"></i>
                                Featured
                            </span>
                        </div>
                    )}

                    {/* Tutor Header */}
                    <div className="d-flex align-items-start mb-4">
                        <div className="tutor-avatar position-relative me-3">
                            <img 
                                src={`https://pastq.eddcodesgh.com/qApi/uploads/tutors/${tutor.profileImage}`} 
                                alt={tutor.name}
                                className="rounded-circle"
                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                onError={(e) => {
                                    e.target.src = "/assets/img/default-avatar.jpg";
                                }}
                            />
                            {tutor.verified && (
                                <div className="verified-badge position-absolute bottom-0 end-0">
                                    <i className="bi bi-patch-check-fill text-primary bg-white rounded-circle"></i>
                                </div>
                            )}
                        </div>
                        <div className="flex-grow-1">
                            <h5 className="fw-bold mb-1">{tutor.name}</h5>
                           
                            <p className="text-primary mb-1">{tutor.title}</p>
                            <div className="d-flex align-items-center">
                                <div className="rating me-2">
                                    <i className="bi bi-star-fill text-warning me-1"></i>
                                    <span className="fw-bold">{tutor.rating}</span>
                                    <span className="text-muted ms-1">({tutor.reviews} reviews)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tutor Details */}
                    <div className="tutor-details mb-4">
                        <div className="row mb-2">
                            <div className="col-6">
                                <small className="text-muted d-block">Subjects</small>
                                <div className="subject-tags mt-1">
                                    {tutor.subjects.slice(0, 2).map((subject, idx) => (
                                        <span key={idx} className="badge bg-light text-dark border me-1 mb-1">
                                            {subject}
                                        </span>
                                    ))}
                                    {tutor.subjects.length > 2 && (
                                        <span className="badge bg-light text-dark border">
                                            +{tutor.subjects.length - 2}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="col-6">
                                <small className="text-muted d-block">Hourly Rate</small>
                                <h6 className="fw-bold text-success mb-0">{tutor.hourlyRate}/hr</h6>
                            </div>
                        </div>
                        
                        <div className="row mb-2">
                            <div className="col-6">
                                <small className="text-muted d-block">Experience</small>
                                <span>{tutor.experience}</span>
                            </div>
                            <div className="col-6">
                                <small className="text-muted d-block">Mode</small>
                                <div>
                                    {tutor.mode.map((mode, idx) => (
                                        <span key={idx} className=" mt-1 bg-info bg-opacity-10 text-info border border-info border-opacity-25 me-2">
                                            {mode}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mb-3">
                            <small className="text-muted d-block">Education Levels</small>
                            <div className="level-tags">
                                {tutor.levels.map((level, idx) => (
                                    <span key={idx} className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 me-1">
                                        {level}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <p className="tutor-bio text-muted small">
                            {tutor.bio}
                        </p>
                         <p className="fw-bold small  text-muted">
                            Phone: {tutor.phone} <br />
                            Email: {tutor.email}
                         </p>
                    </div>

                    {/* Tutor Actions */}
                    <div className="tutor-actions">
                        <div className="d-flex gap-2">
                            <button 
                                className="btn btn-outline-primary flex-grow-1"
                                onClick={() => onViewProfile(tutor)}
                            >
                                <i className="bi bi-eye me-2"></i>
                                View Profile
                            </button>
                            <button 
                                className="btn btn-primary flex-grow-1"
                                onClick={() => onBookTutor(tutor)}
                            >
                                <i className="bi bi-calendar-check me-2"></i>
                                Book Now
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ));

    // Reset body scroll when component unmounts
    useEffect(() => {
        if (showBookingModal || selectedTutor) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showBookingModal, selectedTutor]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-md-8 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading verified tutors...</p>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Navbar />
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-md-8">
                            <div className="alert alert-danger">
                                <h5>Error Loading Tutors</h5>
                                <p>{error}</p>
                                <button onClick={fetchVerifiedTutors} className="btn btn-primary">
                                    Try Again
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            
            {/* Hero Section */}
            <section className="py-5 bg-primary mt-4 bg-opacity-10">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-8">
                            
                            <h1 className="display-5 fw-bold mb-3">
                                Learn from <span className="text-primary">Verified</span> Expert Tutors
                            </h1>
                            <p className="lead text-muted mb-4">
                                Connect with qualified, background-checked tutors across Ghana. 
                                Personalized one-on-one sessions to help you excel in your studies.
                            </p>
                            
                            <div className="d-flex flex-wrap gap-3">
                                <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-3 py-2">
                                    <i className="bi bi-shield-check me-2"></i>
                                    All Tutors Verified
                                </span>
                                <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2">
                                    <i className="bi bi-star-fill me-2"></i>
                                    {stats.averageRating} Average Rating
                                </span>
                                <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-3 py-2">
                                    <i className="bi bi-clock-history me-2"></i>
                                    Flexible Scheduling
                                </span>
                            </div>
                        </div>
                        
                        <div className="col-lg-4 mt-4 mt-lg-0">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body text-center">
                                    <i className="bi bi-people display-1 text-primary mb-3"></i>
                                    <h5 className="card-title mb-3">Become a Tutor</h5>
                                    <p className="text-muted mb-4">
                                        Join our platform and share your knowledge with students across Ghana
                                    </p>
                                    <Link to="/tutors/apply" className="btn btn-primary w-100">
                                        <i className="bi bi-person-plus me-2"></i>
                                        Apply as Tutor
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            

            {/* Search & Filter Section */}
            <section className="py-4 bg-light">
                <div className="container">
                    <div className="row g-3">
                        <div className="col-lg-4">
                            <div className="input-group">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Search tutors by name, subject, or expertise..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        
                        <div className="col-lg-4">
                            <select 
                                className="form-select"
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                            >
                                <option value="all">All Subjects</option>
                                {subjects.map((subject, index) => (
                                    <option key={index} value={subject}>{subject}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="col-lg-4">
                            <select 
                                className="form-select"
                                value={selectedLevel}
                                onChange={(e) => setSelectedLevel(e.target.value)}
                            >
                                <option value="all">All Education Levels</option>
                                {educationLevels.map((level, index) => (
                                    <option key={index} value={level}>{level}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    <div className="mt-3">
                        <small className="text-muted">
                            Showing {filteredTutors.length} of {tutors.length} tutors
                            {selectedSubject !== "all" && ` in ${selectedSubject}`}
                            {selectedLevel !== "all" && ` for ${selectedLevel}`}
                        </small>
                    </div>
                </div>
            </section>

            {/* Tutors Grid */}
            <section className="py-5">
                <div className="container">
                    {filteredTutors.length === 0 ? (
                        <div className="text-center py-5">
                            <i className="bi bi-person-x display-1 text-muted"></i>
                            <h4 className="mt-3">No Tutors Found</h4>
                            <p className="text-muted mb-4">
                                Try adjusting your search filters
                            </p>
                            <button 
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    setSearchTerm("");
                                    setSelectedSubject("all");
                                    setSelectedLevel("all");
                                }}
                            >
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                Reset Filters
                            </button>
                        </div>
                    ) : (
                        <div className="row">
                            {filteredTutors.map((tutor) => (
                                <TutorCard 
                                    key={tutor.id} 
                                    tutor={tutor} 
                                    onViewProfile={setSelectedTutor}
                                    onBookTutor={handleBookTutor}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* How It Works */}
            <section className="py-5 bg-light">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 text-center mb-5">
                            <h2 className="fw-bold mb-3">How It Works</h2>
                            <p className="lead text-muted">
                                Get started with expert tutoring in 3 simple steps
                            </p>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 mb-4">
                            <div className="card border-0 bg-white h-100 text-center p-4">
                                <div className="step-number mb-4">1</div>
                                <i className="bi bi-search display-4 text-primary mb-3"></i>
                                <h5>Find Your Tutor</h5>
                                <p className="text-muted">
                                    Browse verified tutors, read reviews, and check their qualifications
                                </p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="card border-0 bg-white h-100 text-center p-4">
                                <div className="step-number mb-4">2</div>
                                <i className="bi bi-calendar-check display-4 text-success mb-3"></i>
                                <h5>Book a Session</h5>
                                <p className="text-muted">
                                    Schedule a session at your convenience with flexible timing options
                                </p>
                            </div>
                        </div>
                        <div className="col-md-4 mb-4">
                            <div className="card border-0 bg-white h-100 text-center p-4">
                                <div className="step-number mb-4">3</div>
                                <i className="bi bi-chat-dots display-4 text-warning mb-3"></i>
                                <h5>Start Learning</h5>
                                <p className="text-muted">
                                    Connect with your tutor online or in-person and begin your learning journey
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Tutor Detail Modal */}
            {selectedTutor && (
                <div className="row">
                    <div className="col-md-12">
                        <div className="modal-backdrop-custom mt-5" onClick={() => setSelectedTutor(null)}>
                            <div className="modal-dialog-custom mt-5" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-content-custom">
                                    <div className="modal-header border-bottom">
                                        <h4 className="modal-title m-0">Tutor Profile</h4>
                                        <button 
                                            type="button"
                                            className="btn btn-primary"
                                            style={{ fontSize: "50" }}
                                            onClick={() => setSelectedTutor(null)}
                                            aria-label="Close"
                                        >x</button>
                                    </div>
                                    
                                    <div className="modal-body-custom">
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="text-center">
                                                    <img 
                                                        src={`https://pastq.eddcodesgh.com/qApi/uploads/tutors/${selectedTutor.profileImage}`}
                                                        alt={selectedTutor.name}
                                                        className="rounded-circle mb-3 img-fluid"
                                                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                                        onError={(e) => {
                                                            e.target.src = "/assets/img/default-avatar.jpg";
                                                        }}
                                                    />
                                                    <h5 className="fw-bold">{selectedTutor.name}</h5>
                                                    <p className="text-primary">{selectedTutor.title}</p>
                                                    <div className="mb-3">
                                                        <i className="bi bi-star-fill text-warning"></i>
                                                        <span className="fw-bold ms-1">{selectedTutor.rating}</span>
                                                        <span className="text-muted ms-1">({selectedTutor.reviews} reviews)</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-8">
                                                <div className="row mb-3">
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Hourly Rate</small>
                                                        <h4 className="fw-bold text-success">{selectedTutor.hourlyRate}/hr</h4>
                                                    </div>
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Experience</small>
                                                        <h5>{selectedTutor.experience}</h5>
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-3">
                                                    <small className="text-muted d-block">Education</small>
                                                    <p>{selectedTutor.education}</p>
                                                </div>
                                                
                                                <div className="mb-3">
                                                    <small className="text-muted d-block">Subjects</small>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {selectedTutor.subjects.map((subject, idx) => (
                                                            <span key={idx} className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                                                                {subject}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-3">
                                                    <small className="text-muted d-block">Teaching Modes</small>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {selectedTutor.mode.map((mode, idx) => (
                                                            <span key={idx} className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">
                                                                {mode}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                <div className="mb-4">
                                                    <small className="text-muted d-block">Availability</small>
                                                    <p>{selectedTutor.availability}</p>
                                                </div>
                                                
                                                <div className="mb-4">
                                                    <small className="text-muted d-block">About</small>
                                                    <p>{selectedTutor.bio}</p>
                                                </div>
                                                
                                                <div className="d-flex gap-2">
                                                    <button 
                                                        className="btn btn-outline-primary flex-grow-1"
                                                        onClick={() => {
                                                            setSelectedTutor(null);
                                                            handleBookTutor(selectedTutor);
                                                        }}
                                                    >
                                                        <i className="bi bi-calendar-check me-2"></i>
                                                        Book Session
                                                    </button>
                                                    <button 
                                                        className="btn btn-primary flex-grow-1"
                                                        onClick={() => {
                                                            toast.success(`${selectedTutor.name} added to favorites!`);
                                                            setSelectedTutor(null);
                                                        }}
                                                    >
                                                        <i className="bi bi-heart me-2"></i>
                                                        Add to Favorites
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Booking Modal */}
            {showBookingModal && selectedTutor && (
                <div className="modal-backdrop-custom" onClick={() => setShowBookingModal(false)}>
                    <div className="modal-dialog-custom modal-dialog-sm mt-5 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content-custom mt-5">
                            <div className="modal-header border-bottom">
                                <h4 className="modal-title m-0">Book Tutoring Session</h4>
                                <button 
                                    type="button"
                                    className="btn btn-primary"
                                    style={{ fontSize: "50" }}
                                    onClick={() => setShowBookingModal(false)}
                                    aria-label="Close"
                                >x</button>
                            </div>
                            
                            <div className="modal-body-custom">
                                <div className="tutor-info mb-4">
                                    <div className="d-flex align-items-center">
                                        <img 
                                            src={`https://pastq.eddcodesgh.com/qApi/uploads/tutors/${selectedTutor.profileImage}`}
                                            alt={selectedTutor.name}
                                            className="rounded-circle me-3"
                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                            onError={(e) => {
                                                e.target.src = "/assets/img/default-avatar.jpg";
                                            }}
                                        />
                                        <div>
                                            <h6 className="fw-bold mb-0">{selectedTutor.name}</h6>
                                            <small className="text-muted">{selectedTutor.title}</small>
                                        </div>
                                    </div>
                                </div>
                                
                                <form onSubmit={handleBookingSubmit}>
                                    <div className="mb-3">
                                         <label className="form-label">Phone Number</label>
                                            <input
                                                type="tel"
                                                className="form-control"
                                                value={bookingForm.phone}
                                                onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                                                required
                                                placeholder="Enter your mobile number"
                                            />
                                        
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Subject</label>
                                        <select 
                                            className="form-select"
                                            value={bookingForm.subject}
                                            onChange={(e) => setBookingForm({...bookingForm, subject: e.target.value})}
                                            required
                                        >
                                            <option value="">Select Subject</option>
                                            {selectedTutor.subjects.map((subject, idx) => (
                                                <option key={idx} value={subject}>{subject}</option>
                                            ))}
                                        </select>
                                    </div>
                                    
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={bookingForm.date}
                                                onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                                                required
                                                min={new Date().toISOString().split('T')[0]}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Time</label>
                                            <input
                                                type="time"
                                                className="form-control"
                                                value={bookingForm.time}
                                                onChange={(e) => setBookingForm({...bookingForm, time: e.target.value})}
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <label className="form-label">Duration (hours)</label>
                                            <select 
                                                className="form-select"
                                                value={bookingForm.duration}
                                                onChange={(e) => setBookingForm({...bookingForm, duration: e.target.value})}
                                                required
                                            >
                                                <option value="1">1 hour</option>
                                                <option value="1.5">1.5 hours</option>
                                                <option value="2">2 hours</option>
                                                <option value="2.5">2.5 hours</option>
                                                <option value="3">3 hours</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Session Mode</label>
                                            <select 
                                                className="form-select"
                                                value={bookingForm.mode}
                                                onChange={(e) => setBookingForm({...bookingForm, mode: e.target.value})}
                                                required
                                            >
                                                <option value="online">Online</option>
                                                <option value="in-person">In-person</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-4 p-3 bg-light rounded">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Hourly Rate:</span>
                                            <span className="fw-bold">{selectedTutor.hourlyRate}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Estimated Total:</span>
                                            <span className="fw-bold text-success">
                                                ₵{parseInt(selectedTutor.hourlyRate.replace('₵', '')) * parseFloat(bookingForm.duration)}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button 
                                            type="button"
                                            className="btn btn-outline-secondary flex-grow-1"
                                            onClick={() => setShowBookingModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="submit"
                                            className="btn btn-primary flex-grow-1"
                                        >
                                            <i className="bi bi-calendar-check me-2"></i>
                                            Confirm Booking
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />

            <style jsx>{`
                /* Prevent body scroll when modal is open */
                .modal-open {
                    overflow: hidden;
                    position: fixed;
                    width: 100%;
                }

                .hero-section {
                    background: linear-gradient(135deg, rgba(13, 110, 253, 0.05) 0%, rgba(111, 66, 193, 0.05) 100%);
                }
                
                .tutor-card {
                    transition: transform 0.3s ease;
                    border-radius: 15px;
                }
                
                .tutor-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
                }
                
                .tutor-avatar img {
                    border: 3px solid #fff;
                    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
                }
                
                .verified-badge i {
                    font-size: 1.2rem;
                    padding: 2px;
                }
                
                .tutor-bio {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                
                .step-number {
                    width: 40px;
                    height: 40px;
                    background: var(--bs-primary);
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    margin: 0 auto;
                }
                
                .subject-tags .badge,
                .level-tags .badge {
                    font-size: 0.75rem;
                    padding: 0.25rem 0.5rem;
                }
                
                /* Custom Modal Styles */
                .modal-backdrop-custom {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    z-index: 1050;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    animation: fadeIn 0.15s ease-out;
                    backdrop-filter: blur(2px);
                }
                
                .modal-dialog-custom {
                    margin: 0;
                    width: 100%;
                    max-width: 800px;
                    animation: slideIn 0.2s ease-out;
                }
                
                .modal-dialog-custom.modal-dialog-sm {
                    max-width: 500px;
                }
                
                .modal-content-custom {
                    background-color: white;
                    border-radius: 15px;
                    overflow: hidden;
                    max-height: 90vh;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }
                
                .modal-body-custom {
                    overflow-y: auto;
                    max-height: calc(90vh - 120px);
                    padding: 1.5rem;
                    scroll-behavior: smooth;
                    -webkit-overflow-scrolling: touch;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                @keyframes slideIn {
                    from { 
                        opacity: 0;
                        transform: translateY(-10px) scale(0.98);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                
                /* Fix for iOS bounce effect */
                @supports (-webkit-touch-callout: none) {
                    .modal-backdrop-custom {
                        height: -webkit-fill-available;
                    }
                }
                
                @media (max-width: 768px) {
                    .tutor-card {
                        margin-bottom: 1.5rem;
                    }
                    
                    .modal-dialog-custom {
                        margin: 10px;
                        width: calc(100% - 20px) !important;
                    }
                    
                    .hero-section h1 {
                        font-size: 2rem;
                    }
                    
                    .hero-section p.lead {
                        font-size: 1rem;
                    }
                }
            `}</style>
        </>
    );
};

export default VerifiedTutors;